import moment from 'moment';
import { isValidCron } from 'cron-validator';
import { memoize, get } from 'lodash';
import shortid from 'shortid';
import { timezones } from '../../utils/constants';

const tldjs = require('tldjs');

export const required = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? undefined : 'Required';
  } return value ? undefined : 'Required';
};

export const hasReference = (value) => {
  if (value) {
    return value;
  }
  return shortid.generate();
};

export const maxLength = memoize(max => value => {
  return value && value.length > max ? `Must be ${max} characters or less` : undefined;
});

export const minLength = memoize(min => value => {
  return value && value.length < min ? `Must be ${min} characters or more` : undefined;
});

export const number = (value) => {
  return value && isNaN(Number(value)) ? 'Must be a number' : undefined;
};

export const integer = (value) => {
  return value && !Number.isInteger(+value) ? 'Must be an integer' : undefined;
};

export const maxValue = memoize(max => value => {
  return value && value > max ? `Must be not more than ${max}` : undefined;
});

export const minValue = memoize(min => value => {
  return value && value < min ? `Must be at least ${min}` : undefined;
});

export const email = value => {
  return value && !/.+@.+\..+/i.test(value) ?
    'Invalid email address' : undefined;
};

export const domain = value => {
  return value && tldjs.tldExists(value) && ((tldjs.getSubdomain(value) || '').length === 0) ?
    undefined : 'Invalid domain';
};

export const url = value => {
  return value && !/^(http(s)?:\/\/)[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.%]+$/i.test(value) ?
    'Invalid URL. You may need to include "http://" in the URL' : undefined;
};

export const alphanumeric = value => {
  return value && !/^[a-zA-Z0-9]*$/.test(value) ?
    'Must be alphanumeric' : undefined;
};

export const password = value => {
  return value &&
    /(?=^.{8,}$)((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/.test(value) ?
    undefined :
    'Must contain at least: 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number or special character';
};

export const passwordBlank = value => {
  return !value ? undefined : password(value);
};

export const dateGtnToday = (value) => {
  return moment(value).isSameOrAfter(moment(), 'day') ? undefined : 'Must be greater than today';
};

export const dateGtnDate = (dateBefore, dateAfter) => {
  return dateAfter === null || moment(dateBefore).isAfter(moment(dateAfter), 'day') ?
    undefined : `Must be greater than ${moment(dateAfter).format('MM/DD/YYYY')}`;
};

export const dateBfrToday = (value) => {
  return moment(value).isSameOrBefore(moment(), 'day') ? undefined : 'Must be before than today';
};

export const matchField = memoize((field, resource = null) => (value, allValues) => {
  const matchWith = resource ? `${resource}.${field}` : field;
  return value !== get(allValues, matchWith) ? `Must match ${field}` : undefined;
});

export const includedIn = memoize(allValues => value => {
  return allValues.includes(value) ? undefined : `Must match one of ${allValues}`;
});

export const ifNotBlank = memoize((field, resource = null) => (value, allValues) => {
  const matchWith = resource ? `${resource}.${field}` : field;
  const cond = value ? false : get(allValues, matchWith, '').length > 0;
  return cond ? 'Required' : undefined;
});

export const isValidSlug = value => {
  const keywords = [
    'new',
    'edit',
    'index',
    'session',
    'login',
    'logout',
    'users',
    'admin',
    'stylesheets',
    'assets',
    'javascripts',
    'images',
    'accounts',
    'jobs',
    'websites',
    'campaigns',
    'ads',
    'cnames',
    'c_names'
  ];
  return keywords.includes(value ? value.toLowerCase() : '') ?
    'Is a protected keyword, add additional characters' : undefined;
};

export const cron = (value) => {
  if (isValidCron(value)) { return undefined; }
  return 'Cron is not valid';
};

export const timezone = (value) => {
  if (timezones.includes(value)) { return undefined; }
  return 'Timezone is not valid';
};

export const isUnique = (value, values) => {
  if (values.filter(v => v === value).length < 2) { return undefined; }
  return 'Value must be unique and it has already been used';
};
